if (!customElements.get('subscription-builder')) {
  class SubscriptionBuilder extends HTMLElement {
    connectedCallback() {
      // Theme editor sections may be inserted after this element is registered.
      queueMicrotask(() => {
        if (!this.isConnected || this.events) return;
        const data = this.querySelector('[data-subscription-data]');
        if (!data) return;
        if (!this.initialized) {
          this.data = JSON.parse(data.textContent);
          // the Add-ons blocks ship their extras as JSON islands (settings
          // aren't readable from the section's Liquid); merge every island
          // so a second Add-ons block isn't silently ignored
          const addonIslands = [...this.querySelectorAll('[data-subscription-addons-data]')];
          if (addonIslands.length) {
            const seen = new Set();
            this.data.addons = addonIslands.flatMap(island => JSON.parse(island.textContent))
              .filter(product => !seen.has(product.id) && seen.add(product.id));
          }
          this.strings = this.data.strings;
          this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {
            style: 'currency', currency: this.dataset.currency || 'USD'
          });
          this.pages = [...this.querySelectorAll('[data-subscription-page]')];
          this.questions = this.pages.filter(page => page.dataset.subscriptionPage === 'question');
          // Use the same flow in the editor and storefront. Incomplete
          // questions stay in pages so block selection can still preview them.
          this.questions = this.questions.filter(page =>
            page.querySelector('.heading') && page.querySelectorAll('[data-subscription-answer]').length >= 2
          );
          this.questions.forEach(page => {
            page.dataset.label ||= page.querySelector('.heading')?.textContent.trim() || this.strings.choose;
          });
          // the section's own scheme class; a page with a custom scheme
          // swaps it out while that page is active (see render)
          this.defaultScheme = [...this.classList].find(cls => cls.startsWith('color-')) || '';
          this.activeScheme = this.defaultScheme;
          // the start page is optional (hidden or removed in the editor):
          // without it the flow begins directly at its first stage
          this.hasStart = this.pages.some(page => page.dataset.subscriptionPage === 'start');
          // the summary is optional too: without it the last stage submits
          // directly instead of routing to a blank, pageless step
          this.hasSummary = this.pages.some(page => page.dataset.subscriptionPage === 'summary');
          this.initialized = true;
          this.reset();
        }
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.addEventListener('click', event => this.onClick(event), options);
        this.addEventListener('change', event => this.onChange(event), options);
        this.addEventListener('input', event => {
          if (event.target.matches('[data-subscription-quantity]')) this.onChange(event);
        }, options);
        this.addEventListener('keydown', event => {
          if (event.target.matches('[data-subscription-answer]') && ['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            event.target.click();
          }
        }, options);
        document.addEventListener('shopify:block:select', event => {
          if (!this.contains(event.target) || this.busy) return;
          const page = event.target.closest('[data-subscription-page]') || event.target.querySelector('[data-subscription-page]');
          if (page) this.go(page.dataset.subscriptionPage === 'question' ? this.questionId(page) : page.dataset.subscriptionPage, page);
        }, options);
        window.addEventListener('resize', () => {
          this.syncHeaderOffset();
          // widths changed, so remeasure the page heights from scratch
          clearTimeout(this.equalizeTimer);
          this.equalizeTimer = setTimeout(() => {
            this.flowMinHeight = 0;
            // Fall back to min-height: auto so measurements exclude the old
            // minimum and any numeric min-height transition still in progress.
            this.style.removeProperty('--subscription-flow-min-height');
            this.equalizeHeights();
          }, 150);
        }, options);
        this.syncHeaderOffset();
        this.setAttribute('data-ready', 'true');
        this.render();
      });
    }

    // when the header is in flow (not an absolute overlay) and only the
    // header sits above this section, a 100vh min-height overflows the
    // screen by the header's height - publish that height so the CSS can
    // subtract it (same header-position convention as main-cart.js)
    syncHeaderOffset() {
      let offset = 0;
      const header = document.querySelector('custom-header');
      if (header && window.getComputedStyle(header).position !== 'absolute') {
        const headerHeight = header.offsetHeight;
        const sectionTop = Math.round(this.getBoundingClientRect().top + window.scrollY);
        if (sectionTop <= headerHeight + 10) offset = headerHeight;
      }
      this.style.setProperty('--subscription-header-offset', `${offset}px`);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }

    reset() {
      // stale rendered stages (checked add-ons, typed quantities) must not
      // survive a restart - drop the in-place render caches
      this.querySelectorAll('[data-stage-content]').forEach(content => delete content.dataset.rendered);
      this.answers = new Map();
      this.extras = new Map();
      this.productId = null;
      this.variantId = null;
      this.planId = null;
      this.quantity = 1;
      this.current = 'start';
      this.busy = false;
    }

    // hold every page to the tallest page's height (a high-water mark, so
    // the section never shrinks mid-flow) to stop the section from jumping
    // between stages; hidden pages are measured invisibly in place
    equalizeHeights() {
      if (!this.pages.length) return;
      // desktop only: stacked mobile pages differ too much in height - a
      // high-water minimum would strand short steps in huge blank space
      // (the CSS zeroes the min under 992px as well)
      if (window.matchMedia('(max-width: 992px)').matches) return;
      const flow = this.querySelector('.subscription__flow');
      if (!flow) return;
      // measure natural sizes without the previously applied minimum
      this.style.removeProperty('--subscription-flow-min-height');
      let tallest = 0;
      let activeHeight = 0;
      this.pages.forEach(page => {
        if (!page.hidden) {
          activeHeight = page.offsetHeight;
          tallest = Math.max(tallest, activeHeight);
          return;
        }
        page.style.visibility = 'hidden';
        page.style.position = 'absolute';
        page.style.insetInline = '0';
        page.hidden = false;
        tallest = Math.max(tallest, page.offsetHeight);
        page.hidden = true;
        page.style.removeProperty('visibility');
        page.style.removeProperty('position');
        page.style.removeProperty('inset-inline');
      });
      // the minimum lives on the flow container (tallest page + the chrome
      // around the current one), not on the pages themselves - pages keep
      // their natural height, so the navigation hugs the content and the
      // spare space falls around the whole centered group instead of
      // between the options and the buttons
      const chrome = flow.offsetHeight - activeHeight;
      this.flowMinHeight = Math.max(this.flowMinHeight || 0, tallest + chrome);
      this.style.setProperty('--subscription-flow-min-height', `${this.flowMinHeight}px`);
    }

    questionId(page) { return `question:${page.dataset.questionId}`; }
    variants(product) { return (product?.variants || []).filter(variant => variant.available && variant.plans.length); }
    // every subscription variant, sold out included - shown disabled so
    // shoppers see the full range instead of options silently vanishing
    displayVariants(product) { return (product?.variants || []).filter(variant => variant.plans.length); }
    get product() { return this.data.products.find(product => String(product.id) === this.productId); }
    get variant() { return this.variants(this.product).find(variant => String(variant.id) === this.variantId); }
    get plan() { return this.variant?.plans.find(plan => String(plan.id) === this.planId); }
    // Custom money formats apply only to the shop's base currency. Prices
    // are already in the cart currency, so use Intl for other currencies.
    format(cents) {
      const format = this.dataset.moneyFormat;
      const amount = cents / 100;
      if (!format || this.dataset.currency !== this.dataset.shopCurrency) return this.money.format(amount);
      const placeholder = format.match(/{{\s*(amount(?:_[a-z_]+)?)\s*}}/);
      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.'],
      };
      const delimiters = formats[placeholder?.[1]];
      if (!delimiters) return this.money.format(amount);
      const [precision, thousands, decimal] = delimiters;
      const parts = amount.toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return format.replace(placeholder[0], parts.join(decimal)).trim();
    }

    visibleQuestions() {
      // a conditional question is visible once any earlier visible question
      // was answered with the value it names - no key plumbing needed.
      // Both sides are trimmed and lowercased (same forgiveness as the
      // filter-tag matching), so 'Gift' vs 'gift ' still connects.
      const norm = value => (value || '').trim().toLowerCase();
      const earlier = new Set();
      return this.questions.filter(page => {
        const condition = norm(page.dataset.conditionValue);
        const visible = !condition || earlier.has(condition);
        if (!visible) this.answers.delete(this.questionId(page));
        const answer = this.answers.get(this.questionId(page));
        if (visible && answer) earlier.add(norm(answer.value));
        return visible;
      });
    }

    products() {
      const tags = this.visibleQuestions().map(page => this.answers.get(this.questionId(page))?.tag).filter(Boolean);
      return this.data.products.filter(product => this.displayVariants(product).length &&
        tags.every(tag => product.tags.some(value => value.toLowerCase() === tag.toLowerCase())));
    }

    addonProducts() {
      return this.data.addons.filter(product => !product.requiresPlan && product.variants.some(variant => variant.available));
    }

    reconcile() {
      if (!this.products().some(product => String(product.id) === this.productId)) {
        this.productId = null;
        this.variantId = null;
        this.planId = null;
        this.quantity = 1;
      }
    }

    flow() {
      const questions = new Set(this.visibleQuestions());
      const stages = [];
      this.pages.forEach(page => {
        const kind = page.dataset.subscriptionPage;
        if (kind === 'start' || kind === 'summary') return;
        if (kind === 'question' && !questions.has(page)) return;
        if (kind === 'variant' && this.product && this.displayVariants(this.product).length === 1) return;
        if (kind === 'addons' && !this.addonProducts().length) return;
        const id = kind === 'question' ? this.questionId(page) : kind;
        // A purchase stage represents one selection even if duplicated in the editor.
        if (!stages.includes(id)) stages.push(id);
      });
      // variant/quantity/frequency cannot work before a product is chosen:
      // reorder a misplaced block instead of dead-ending the shopper, and
      // drop them entirely when there is no product stage at all
      const dependent = ['variant', 'quantity', 'frequency'];
      const productAt = stages.indexOf('product');
      if (productAt === -1) {
        for (const id of dependent) {
          const at = stages.indexOf(id);
          if (at !== -1) stages.splice(at, 1);
        }
      } else if (dependent.some(id => stages.includes(id) && stages.indexOf(id) < stages.indexOf('product'))) {
        const present = dependent.filter(id => stages.includes(id));
        const rest = stages.filter(id => !present.includes(id));
        rest.splice(rest.indexOf('product') + 1, 0, ...present);
        stages.length = 0;
        stages.push(...rest);
      }
      if (this.hasSummary || !stages.length) stages.push('summary');
      return stages;
    }

    pageFor(id) {
      if (this.previewPage && this.current === id) return this.previewPage;
      return this.pages.find(page => (page.dataset.subscriptionPage === 'question' ? this.questionId(page) : page.dataset.subscriptionPage) === id);
    }

    labelFor(id) { return this.pageFor(id)?.dataset.label || ''; }

    validQuantity(value, variant) {
      return Boolean(variant && Number.isSafeInteger(value) && value >= variant.min &&
        (variant.max == null || value <= variant.max) && (value - variant.min) % variant.increment === 0);
    }

    valid(id) {
      if (id.startsWith('question:')) return this.answers.has(id);
      if (id === 'product') return Boolean(this.product);
      if (id === 'variant') return Boolean(this.variant);
      if (id === 'quantity') return this.validQuantity(this.quantity, this.variant);
      if (id === 'frequency') return Boolean(this.plan);
      // extras carry no quantity input - always added at the variant minimum
      if (id === 'addons') return true;
      // Removed or unfinished blocks must not allow an invalid cart request.
      if (id === 'summary') return Boolean(this.product && this.variant && this.plan && this.validQuantity(this.quantity, this.variant));
      return true;
    }

    updateSubmitButton(flow = this.flow()) {
      this.querySelector('[data-subscription-submit]').disabled = this.busy || flow.some(id => !this.valid(id)) || !this.valid('summary');
    }

    error(message = '') {
      const element = this.querySelector('[data-subscription-error]');
      element.textContent = message;
      element.hidden = !message;
    }

    go(id, previewPage = null) {
      this.current = id;
      this.previewPage = previewPage;
      this.error();
      this.render();
      const page = this.pageFor(id);
      const heading = page?.querySelector('[data-stage-heading], .heading');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        if (this.getBoundingClientRect().top < 0) {
          // the shopper was scrolled deep into the previous step (a long
          // product grid, say): bring them back to the top of the section
          // so the next question starts in view, under any sticky header
          const header = document.querySelector('custom-header .header[data-sticky="true"], custom-header[data-sticky="true"]');
          const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({
            top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - (header ? header.offsetHeight : 0)),
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        } else if (typeof heading.scrollIntoView === 'function') {
          heading.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }
    }

    onClick(event) {
      const target = event.target.closest('button, .btn-quantity, [data-subscription-start], [data-subscription-answer]');
      if (!target || !this.contains(target)) return;
      if (this.busy) { event.preventDefault(); return; }
      if (target.matches('.btn-quantity')) {
        const input = target.parentElement.querySelector('input[type="number"]');
        if (input) {
          // stepUp/stepDown clamp to the variant's min/max and step rule
          if (target.dataset.action === 'add') input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      if (target.matches('[data-subscription-start]')) {
        event.preventDefault();
        this.go(this.flow()[0]);
      } else if (target.matches('[data-subscription-answer]')) {
        const page = target.closest('[data-subscription-page]');
        this.answers.set(this.questionId(page), { value: target.dataset.value, label: target.dataset.label, tag: target.dataset.filterTag.trim() });
        this.reconcile();
        this.error();
        this.render();
      } else if (target.dataset.select) {
        const id = target.dataset.value;
        if (target.dataset.select === 'product' && this.productId !== id) {
          this.productId = id;
          const variants = this.variants(this.product);
          this.variantId = variants.length === 1 ? String(variants[0].id) : null;
          this.quantity = this.variant?.min || 1;
          this.planId = null;
        } else if (target.dataset.select === 'variant' && this.variantId !== id) {
          this.variantId = id;
          this.quantity = this.variant.min;
          this.planId = null;
        } else if (target.dataset.select === 'frequency') {
          this.planId = id;
        }
        this.error();
        this.render();
      } else if (target.matches('[data-subscription-next]')) {
        if (!this.valid(this.current)) {
          this.error(this.current === 'quantity' || this.current === 'addons' ? this.strings.invalidQuantity : this.strings.choose);
          return;
        }
        const flow = this.flow();
        this.go(flow[flow.indexOf(this.current) + 1] || 'summary');
      } else if (target.matches('[data-subscription-back]')) {
        const flow = this.flow();
        this.go(flow[flow.indexOf(this.current) - 1] || (this.hasStart ? 'start' : flow[0]));
      } else if (target.matches('[data-subscription-restart]')) {
        this.reset();
        this.go(this.hasStart ? 'start' : this.flow()[0]);
      } else if (target.dataset.go) {
        this.go(target.dataset.go);
      } else if (target.matches('[data-subscription-submit]')) {
        this.submit();
      }
    }

    onChange(event) {
      if (this.busy) return;
      const input = event.target;
      if (input.matches('[data-subscription-quantity]')) {
        this.quantity = input.valueAsNumber;
        this.error();
        this.updateSubmitButton();
      }
      const card = input.closest('[data-addon-product]');
      if (!card) return;
      const product = this.addonProducts().find(product => String(product.id) === card.dataset.addonProduct);
      const variant = product.variants.find(variant => String(variant.id) === card.querySelector('select').value && variant.available);
      card.querySelector('[data-addon-price]').textContent = this.addonPrice(variant);
      const checked = card.querySelector('[type="checkbox"]').checked;
      // the check icon's visibility is driven by data-checked on the label
      card.querySelector('.subscription-addon-toggle').dataset.checked = String(checked);
      // extras have no quantity input - each is added at the variant's minimum
      if (checked) {
        this.extras.set(String(product.id), { product, variant, quantity: variant.min || 1 });
      } else {
        this.extras.delete(String(product.id));
      }
      this.error();
    }

    node(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    }

    option(kind, value, title, description, selected, image, disabled) {
      const button = this.node('button', 'subscription-option');
      button.type = 'button';
      button.dataset.select = kind;
      button.dataset.value = String(value);
      button.setAttribute('aria-pressed', String(selected));
      if (disabled) button.disabled = true;
      if (image) {
        const img = this.node('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 240;
        img.height = 240;
        button.append(img);
      }
      button.append(this.node('span', 'subscription-option-title', title));
      if (description) button.append(this.node('span', 'subscription-option-description', description));
      return button;
    }

    quantityInput(variant, value) {
      const input = this.node('input', 'subscription-quantity');
      input.type = 'number';
      input.required = true;
      input.min = variant?.min || 1;
      input.step = variant?.increment || 1;
      if (variant?.max != null) input.max = variant.max;
      input.value = value;
      input.setAttribute('aria-label', this.strings.quantity);
      return input;
    }

    addonPrice(variant) {
      const quantity = variant.min || 1;
      const total = this.format(variant.price * quantity);
      return quantity > 1 ? `${total} (${this.strings.quantity}: ${quantity})` : total;
    }

    renderStage(page) {
      const content = page?.querySelector('[data-stage-content]');
      if (!content) return;
      const kind = page.dataset.subscriptionPage;
      // selecting an option re-renders; rebuilding the grid then would
      // replace the clicked card mid-transition (a visible blink, images
      // re-decoding). When the option set is unchanged, only sync the
      // pressed states on the existing cards instead.
      const signature = {
        product: () => `product:${this.products().map(product => product.id).join()}`,
        variant: () => `variant:${this.productId}`,
        frequency: () => `frequency:${this.variantId}:${this.quantity}`,
        quantity: () => `quantity:${this.variantId}`,
        addons: () => 'addons',
      }[kind]?.();
      if (signature && content.dataset.rendered === signature) {
        const selected = { product: this.productId, variant: this.variantId, frequency: this.planId };
        content.querySelectorAll('[data-select]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.value === String(selected[button.dataset.select])));
        });
        // Choosing another variant and returning can reset the quantity
        // without changing this stage's cached variant ID.
        const quantityInput = content.querySelector('[data-subscription-quantity]');
        if (quantityInput) quantityInput.value = Number.isFinite(this.quantity) ? this.quantity : '';
        return;
      }
      content.dataset.rendered = signature || '';
      content.replaceChildren();
      const grid = this.node('div', 'subscription-options');
      if (kind === 'product') {
        const products = this.products();
        products.forEach(product => {
          // no purchasable subscription variant left: visible but inert
          const soldOut = !this.variants(product).length;
          grid.append(this.option(kind, product.id, product.title, soldOut ? this.strings.soldOut : '', String(product.id) === this.productId, product.image, soldOut));
        });
        if (!products.length) content.append(this.node('p', '', this.strings.noProducts));
      } else if (kind === 'variant') {
        // variant image (product image fallback) is shown only by the
        // 'Buttons with image' style - CSS hides it for plain buttons
        this.displayVariants(this.product).forEach(variant => {
          const description = variant.available ? '' : (variant.incoming ? `${this.strings.soldOut} · ${this.strings.restocking}` : this.strings.soldOut);
          grid.append(this.option(kind, variant.id, variant.title, description, String(variant.id) === this.variantId, variant.image || this.product?.image, !variant.available));
        });
      } else if (kind === 'frequency') {
        (this.variant?.plans || []).forEach(plan => {
          const description = [plan.description, `${this.strings.planTotal}: ${this.format(plan.price * this.quantity)}`, `${this.strings.perDelivery}: ${this.format((plan.perDelivery ?? plan.price) * this.quantity)}`].filter(Boolean).join(' · ');
          grid.append(this.option(kind, plan.id, plan.name, description, String(plan.id) === this.planId));
        });
        if (!this.variant?.plans.length) content.append(this.node('p', '', this.strings.noPlans));
      } else if (kind === 'quantity') {
        // stepper built on the theme's quantity-selector classes (a plain
        // div, so the theme element's own logic stays out of the way)
        const selector = this.node('div', 'quantity-selector subscription-quantity-selector');
        selector.append(this.node('label', 'subscription-quantity-label', this.strings.quantity));
        const wrapper = this.node('div', 'quantity-wrapper overflow-hidden');
        const minus = this.node('button', 'btn-quantity cursor-pointer');
        minus.type = 'button';
        minus.setAttribute('aria-label', this.strings.decreaseQuantity);
        minus.dataset.action = 'remove';
        const plus = this.node('button', 'btn-quantity cursor-pointer');
        plus.type = 'button';
        plus.setAttribute('aria-label', this.strings.increaseQuantity);
        plus.dataset.action = 'add';
        const input = this.quantityInput(this.variant, this.quantity);
        input.dataset.subscriptionQuantity = '';
        input.classList.add('input-quantity');
        const hint = this.node('p', 'subscription-quantity-hint', this.quantityHint(this.variant));
        hint.id = `SubscriptionQuantityRules-${this.dataset.id || 'builder'}-${this.pages.indexOf(page)}`;
        input.setAttribute('aria-describedby', hint.id);
        wrapper.append(minus, input, plus);
        selector.append(wrapper, hint);
        content.append(selector);
      } else if (kind === 'addons') {
        this.addonProducts().forEach(product => {
          const selected = this.extras.get(String(product.id));
          const variant = selected?.variant || product.variants.find(variant => variant.available);
          const card = this.node('div', 'subscription-addon');
          card.dataset.addonProduct = String(product.id);
          if (product.image) {
            const img = this.node('img');
            Object.assign(img, { src: product.image, alt: '', loading: 'lazy', width: 240, height: 240 });
            card.append(img);
          }
          // the image bleeds to the card edges; everything else sits in a
          // padded content body (at least 1rem - see subscription.css)
          const body = this.node('div', 'subscription-addon__content');
          card.append(body);
          body.append(this.node('h3', '', product.title));
          const label = this.node('label', '', this.strings.variant);
          const select = this.node('select');
          product.variants.filter(variant => variant.available).forEach(variant => {
            const option = this.node('option', '', variant.title);
            option.value = variant.id;
            select.append(option);
          });
          select.value = variant.id;
          label.append(select);
          label.hidden = product.variants.length === 1 && variant.title === 'Default Title';
          const price = this.node('p', '', this.addonPrice(variant));
          price.dataset.addonPrice = '';
          body.append(price);
          body.append(label);
          // theme checkbox pattern (gift-card form, filters): hidden native
          // input + sibling label with a .custom-checkbox box and check icon
          const check = this.node('input', 'checkbox');
          check.type = 'checkbox';
          check.id = `subscription-addon-${product.id}`;
          check.checked = Boolean(selected);
          const checkLabel = this.node('label', 'subscription-addon-toggle d-flex align-items-center cursor-pointer');
          checkLabel.setAttribute('for', check.id);
          checkLabel.dataset.checked = String(Boolean(selected));
          const box = this.node('span', 'custom-checkbox d-flex justify-content-center align-items-center mr-3');
          box.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" width="13px" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke="#6BBD4F" d="M4.5 12.75l6 6 9-13.5"/></svg>';
          checkLabel.append(box, this.node('span', '', this.strings.addExtra));
          body.append(check, checkLabel);
          grid.append(card);
        });
      } else if (kind === 'summary') {
        this.renderSummary(content);
      }
      if (grid.childElementCount) content.append(grid);
    }

    renderSummary(content) {
      const list = this.node('dl', 'subscription-summary');
      const row = (label, value, stage) => {
        const wrapper = this.node('div', 'subscription-summary-row');
        wrapper.append(this.node('dt', '', label), this.node('dd', '', value));
        if (this.flow().includes(stage)) {
          const edit = this.node('button', 'subscription-edit', this.strings.edit);
          edit.type = 'button';
          edit.dataset.go = stage;
          edit.setAttribute('aria-label', `${this.strings.edit}: ${label}`);
          wrapper.append(edit);
        }
        list.append(wrapper);
      };
      this.visibleQuestions().forEach(page => row(page.dataset.label, this.answers.get(this.questionId(page))?.label || '', this.questionId(page)));
      row(this.strings.product, this.product?.title || '', 'product');
      if (this.variants(this.product).length > 1) row(this.strings.variant, this.variant?.title || '', 'variant');
      row(this.strings.quantity, String(this.quantity), 'quantity');
      row(this.strings.frequency, this.plan?.name || '', 'frequency');
      let addonTotal = 0;
      this.extras.forEach(extra => {
        addonTotal += extra.variant.price * extra.quantity;
        const title = extra.variant.title === 'Default Title' ? extra.product.title : `${extra.product.title} — ${extra.variant.title}`;
        row(this.strings.addons, `${title} × ${extra.quantity} (${this.format(extra.variant.price * extra.quantity)})`, 'addons');
      });
      content.append(list);
      if (this.plan) {
        content.append(this.node('p', 'subscription-total', `${this.strings.firstOrder}: ${this.format((this.plan.checkoutCharge ?? this.plan.price) * this.quantity + addonTotal)}`));
        content.append(this.node('p', '', `${this.strings.perDelivery}: ${this.format((this.plan.perDelivery ?? this.plan.price) * this.quantity)}`));
      }
      content.append(this.node('p', 'subscription-checkout-note', this.strings.checkoutNote));
    }

    quantityHint(variant) {
      return this.strings.quantityRule.replace('[min]', variant?.min || 1)
        .replace('[step]', variant?.increment || 1)
        .replace('[max]', variant?.max ?? this.strings.noMaximum);
    }

    render() {
      const flow = this.flow();
      if (this.current === 'start' && !this.hasStart) this.current = flow[0];
      if (this.current !== 'start' && !flow.includes(this.current) && !this.previewPage) this.current = flow[0];
      const focus = document.activeElement?.dataset;
      const focusChoice = focus?.select ? { kind: focus.select, value: focus.value } : null;
      this.pages.forEach(page => { page.hidden = page !== this.pageFor(this.current); });
      // a page with a custom color scheme repaints the whole builder while
      // active (background, navigation and progress included)
      const scheme = this.pageFor(this.current)?.dataset.schemeClass || this.defaultScheme;
      if (scheme && scheme !== this.activeScheme) {
        if (this.activeScheme) this.classList.remove(this.activeScheme);
        this.classList.add(scheme);
        this.activeScheme = scheme;
      }
      this.questions.forEach(page => page.querySelectorAll('[data-subscription-answer]').forEach(answer => {
        answer.setAttribute('aria-pressed', String(this.answers.get(this.questionId(page))?.value === answer.dataset.value));
      }));
      this.renderStage(this.pageFor(this.current));
      if (focusChoice) [...this.querySelectorAll('[data-select]')].find(button => button.dataset.select === focusChoice.kind && button.dataset.value === focusChoice.value)?.focus({ preventScroll: true });
      const atStart = this.current === 'start';
      const index = flow.indexOf(this.current);
      // without a summary page the final stage carries the submit button
      const atSummary = this.current === 'summary' || (!this.hasSummary && index >= 0 && index === flow.length - 1);
      // A selected block can be excluded from the customer flow (for example,
      // an unfinished question). Preview its content without a bogus "Step 0".
      const previewOnly = !atStart && Boolean(this.previewPage) && index < 0;
      this.querySelector('[data-subscription-navigation]').hidden = atStart || previewOnly;
      this.querySelector('[data-subscription-restart]').hidden = atStart || previewOnly;
      // on the first stage there is nowhere back to go unless a start page exists
      this.querySelector('[data-subscription-back]').hidden = !this.hasStart && index <= 0;
      this.querySelector('[data-subscription-next]').hidden = atSummary;
      this.querySelector('[data-subscription-submit]').hidden = !atSummary;
      this.updateSubmitButton(flow);
      this.querySelector('[data-subscription-progress]').hidden = atStart || previewOnly;
      const progress = this.querySelector('[data-progress-list]');
      progress.replaceChildren();
      flow.forEach((id, stepIndex) => {
        const li = this.node('li');
        const button = this.node('button');
        // number and label spans; the navigation style (data-navigation-style
        // on the host) decides which are visible - the aria-label keeps the
        // step name for screen readers in every style
        const number = this.node('span', 'subscription-progress__number', String(stepIndex + 1));
        number.setAttribute('aria-hidden', 'true');
        button.append(number, this.node('span', 'subscription-progress__label', this.labelFor(id)));
        button.setAttribute('aria-label', this.labelFor(id));
        button.type = 'button';
        button.disabled = stepIndex > index || this.busy;
        button.dataset.go = id;
        if (stepIndex === index) button.setAttribute('aria-current', 'step');
        li.append(button);
        progress.append(li);
      });
      this.querySelector('[data-progress-status]').textContent = this.strings.step.replace('[current]', index + 1).replace('[total]', flow.length);
      this.equalizeHeights();
      // images that finish loading after the measure can still change the
      // page's height - remeasure then, so the growth transitions smoothly
      // instead of nudging the centered content mid-paint
      this.querySelectorAll('[data-subscription-page]:not([hidden]) img').forEach(img => {
        if (!img.complete) img.addEventListener('load', () => this.equalizeHeights(), { once: true });
      });
    }

    async submit() {
      if (this.busy) return;
      this.reconcile();
      const incomplete = this.flow().find(id => !this.valid(id));
      if (incomplete) {
        this.go(incomplete);
        this.error(incomplete === 'quantity' || incomplete === 'addons' ? this.strings.invalidQuantity : this.strings.choose);
        return;
      }
      const properties = {};
      this.visibleQuestions().forEach((page, index) => {
        // Include an index so duplicate merchant labels cannot overwrite answers.
        properties[`${index + 1}. ${page.dataset.label}`] = this.answers.get(this.questionId(page)).label;
      });
      const items = [{ id: this.variant.id, quantity: this.quantity, selling_plan: this.plan.id, properties }];
      this.extras.forEach(extra => items.push({ id: extra.variant.id, quantity: extra.quantity }));
      this.busy = true;
      this.error();
      this.setAttribute('aria-busy', 'true');
      const controls = [...this.querySelectorAll('button, input, select')].filter(control => !control.disabled);
      controls.forEach(control => { control.disabled = true; });
      let customerError = '';
      let stayed = false;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          customerError = typeof result.description === 'string' ? result.description : this.strings.error;
          throw new Error(customerError);
        }
        const drawer = document.querySelector('cart-drawer');
        const cartAction = this.pageFor('summary')?.dataset.cartAction || this.dataset.cartAction || 'drawer';
        if (cartAction === 'drawer' && typeof drawer?.refreshFromServer === 'function') {
          this.querySelector('[data-progress-status]').textContent = this.strings.added;
          drawer.refreshFromServer(true);
          stayed = true;
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
      } catch {
        this.error(customerError || this.strings.error);
        this.busy = false;
        this.removeAttribute('aria-busy');
        controls.forEach(control => { control.disabled = false; });
        return;
      }
      if (stayed) {
        // success: reset the finished flow so the summary cannot be
        // re-submitted and the shopper gets a clean start
        this.busy = false;
        this.removeAttribute('aria-busy');
        controls.forEach(control => { control.disabled = false; });
        this.reset();
        this.go(this.hasStart ? 'start' : this.flow()[0]);
      }
      // on redirect, controls stay disabled while the navigation happens
    }
  }

  customElements.define('subscription-builder', SubscriptionBuilder);
}
